import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, MapPin, Building2, UserRound, Crown, Medal, Award, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, avatarUrlOf } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { COUNTRIES } from "@/lib/countries";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AvatarImg } from "@/components/AvatarImg";
import { PageLoader } from "@/components/FindoSpinner";

const YEAR = new Date().getFullYear();

const BADGE_META = {
  bronze: { label: "hc.bronze", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  silver: { label: "hc.silver", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  gold: { label: "hc.gold", cls: "bg-amber-50 text-amber-600 border-amber-300" },
};

function BadgeChip({ badge }) {
  const { t } = useLang();
  if (!badge || !BADGE_META[badge]) return null;
  const m = BADGE_META[badge];
  return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.cls}`} data-testid="row-badge"><Award size={10} /> {t(m.label)}</span>;
}

function rankBadge(rank) {
  if (rank === 1) return <Crown size={18} className="text-amber-400" />;
  if (rank === 2) return <Medal size={18} className="text-slate-400" />;
  if (rank === 3) return <Medal size={18} className="text-amber-700" />;
  return <span className="text-sm font-bold text-slate-400 w-[18px] text-center">{rank}</span>;
}

function countryLabel(code) {
  const c = COUNTRIES.find((x) => x.code === (code || "").toUpperCase());
  return c ? `${c.flag} ${c.name}` : code;
}

export default function Classement() {
  const { t } = useLang();
  const [tab, setTab] = useState("finders");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [finders, setFinders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [champion, setChampion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (country) qs.set("country", country);
    if (city.trim()) qs.set("city", city.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    Promise.all([
      api(`/public/finder-leaderboard${suffix}`, { auth: false }).catch(() => ({ entries: [], champion: null })),
      api(`/public/partner-leaderboard${suffix}`, { auth: false }).catch(() => ({ entries: [] })),
    ]).then(([f, p]) => {
      setFinders(f.entries || []);
      setChampion(f.champion || null);
      setPartners(p.entries || []);
      setLoading(false);
    });
  }, [country, city]);

  function share() {
    const url = window.location.href;
    const text = t("lb.shareText");
    if (navigator.share) {
      navigator.share({ title: "findloop", text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text} ${url}`).then(() => toast.success(t("an.linkCopied"))).catch(() => {});
    }
  }

  const rows = tab === "finders" ? finders : partners;

  return (
    <div className="min-h-full bg-findo-cream">
      <main className="max-w-4xl mx-auto w-full px-6 py-12">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-findo-red bg-findo-red/10 rounded-full px-3 py-1" data-testid="ranking-badge">
            <Trophy size={13} /> {t("lb.badge")} {YEAR}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-findo-navy mt-4">{t("lb.title")}</h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">{t("lb.subtitle")}</p>
          <div className="mt-4">
            <Button onClick={share} variant="outline" size="sm" className="rounded-full font-semibold" data-testid="ranking-share-btn">
              <Share2 size={14} className="mr-1.5" /> {t("lb.share")}
            </Button>
          </div>
        </div>

        {/* Champion of last year */}
        {champion && tab === "finders" && (
          <div className="mt-6 max-w-lg mx-auto rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-4 flex items-center gap-4" data-testid="champion-banner">
            <div className="h-12 w-12 rounded-full overflow-hidden bg-white border border-amber-200 grid place-items-center shrink-0">
              {avatarUrlOf(champion) ? <AvatarImg src={avatarUrlOf(champion)} alt="" className="h-full w-full object-cover" /> : <Crown size={22} className="text-amber-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600">{t("lb.champion")} {champion.year}</p>
              <p className="font-semibold text-findo-navy truncate">{champion.name} · {champion.score} {t("lb.pts")}</p>
            </div>
            <Crown size={26} className="text-amber-400 shrink-0" />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex justify-center gap-2" data-testid="ranking-tabs">
          <TabBtn active={tab === "finders"} onClick={() => setTab("finders")} icon={UserRound} label={t("lb.tabFinders")} testid="tab-finders" />
          <TabBtn active={tab === "partners"} onClick={() => setTab("partners")} icon={Building2} label={t("lb.tabPartners")} testid="tab-partners" />
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-11 rounded-full border border-border bg-white px-4 text-sm" data-testid="ranking-filter-country">
            <option value="">{t("lb.allCountries")}</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>)}
          </select>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("lb.cityPh")} className="h-11 rounded-full w-52 bg-white" data-testid="ranking-filter-city" />
        </div>

        {/* Podium (finders, top 3) */}
        {!loading && tab === "finders" && rows.length >= 3 && <Podium top3={rows.slice(0, 3)} t={t} />}

        {/* List */}
        <div className="mt-8 space-y-2.5" data-testid="ranking-list">
          {loading ? (
            <PageLoader label={t("c.loading")} className="py-10" size={32} />
          ) : rows.length === 0 ? (
            <div className="text-center text-slate-400 py-16 bg-white rounded-2xl border border-border" data-testid="ranking-empty">
              <Trophy size={40} className="mx-auto text-slate-200" />
              <p className="mt-3 font-semibold">{t("lb.empty")}</p>
            </div>
          ) : tab === "finders" ? (
            rows.map((r) => <FinderRow key={r.id} r={r} t={t} />)
          ) : (
            rows.map((r) => <PartnerRow key={r.partner_id} r={r} t={t} />)
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8" data-testid="ranking-reset-note">{t("lb.resetNote")}</p>
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-findo-navy text-white" : "bg-white text-slate-600 border border-border hover:bg-slate-50"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}

function FinderRow({ r, t }) {
  return (
    <Link to={`/trouveur/${r.id}`} className={`flex items-center gap-4 bg-white rounded-2xl border card-shadow p-4 transition-transform hover:-translate-y-0.5 hover:shadow-md ${r.rank <= 3 ? "border-amber-200" : "border-border"}`} data-testid={`finder-row-${r.rank}`}>
      <div className="w-8 grid place-items-center shrink-0">{rankBadge(r.rank)}</div>
      <div className="h-11 w-11 rounded-full overflow-hidden bg-findo-cream border border-border grid place-items-center shrink-0">
        {avatarUrlOf(r) ? <AvatarImg src={avatarUrlOf(r)} alt="" className="h-full w-full object-cover" /> : <UserRound size={20} className="text-slate-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-findo-navy truncate">{r.name}</p>
          <BadgeChip badge={r.badge} />
        </div>
        {(r.city || r.country) && (
          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
            <MapPin size={11} /> {[r.city, r.country ? countryLabel(r.country) : null].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="font-display text-2xl font-semibold text-findo-red">{r.score}</p>
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t("lb.pts")}</p>
      </div>
    </Link>
  );
}

function Podium({ top3, t }) {
  // order: 2nd, 1st, 3rd for the classic podium look
  const order = [top3[1], top3[0], top3[2]];
  const heights = ["h-20", "h-28", "h-16"];
  const rings = ["ring-slate-300", "ring-amber-400", "ring-amber-700"];
  return (
    <div className="mt-8 flex items-end justify-center gap-3 sm:gap-6" data-testid="podium">
      {order.map((r, i) => (
        <Link to={`/trouveur/${r.id}`} key={r.id} className="flex flex-col items-center w-24 sm:w-28 group" data-testid={`podium-${r.rank}`}>
          <div className={`h-14 w-14 rounded-full overflow-hidden bg-white border grid place-items-center ring-2 transition-transform group-hover:scale-105 ${rings[i]}`}>
            {avatarUrlOf(r) ? <AvatarImg src={avatarUrlOf(r)} alt="" className="h-full w-full object-cover" /> : <UserRound size={24} className="text-slate-300" />}
          </div>
          <p className="mt-2 text-sm font-semibold text-findo-navy text-center truncate w-full group-hover:underline">{r.name}</p>
          <p className="text-xs font-bold text-findo-red">{r.score} {t("lb.pts")}</p>
          <div className={`mt-2 w-full ${heights[i]} rounded-t-xl bg-gradient-to-b from-findo-navy to-[#0f1e3a] text-white grid place-items-center`}>
            <span className="font-display text-3xl font-semibold">{r.rank}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function PartnerRow({ r, t }) {
  return (
    <div className={`flex items-center gap-4 bg-white rounded-2xl border card-shadow p-4 ${r.rank <= 3 ? "border-amber-200" : "border-border"}`} data-testid={`partner-row-${r.rank}`}>
      <div className="w-8 grid place-items-center shrink-0">{rankBadge(r.rank)}</div>
      <div className="h-11 w-11 rounded-xl overflow-hidden bg-findo-cream border border-border grid place-items-center shrink-0">
        {r.logo_base64 ? <img src={r.logo_base64} alt="" className="h-full w-full object-cover" /> : <Building2 size={20} className="text-slate-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-findo-navy truncate">{r.org_name}</p>
        {(r.city || r.country) && (
          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
            <MapPin size={11} /> {[r.city, r.country ? countryLabel(r.country) : null].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="font-display text-2xl font-semibold text-findo-red">{r.restituted}</p>
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t("lb.returned")}</p>
      </div>
    </div>
  );
}
